import {Component, computed, OnInit, signal, Signal} from '@angular/core';
import {ManageTaBaseComponent} from "../manage-ta-base-component.directive";
import {Button} from "primeng/button";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {TaCostBubbleComponent} from "../../ta-cost-bubble/ta-cost-bubble.component";
import {Textarea} from "primeng/textarea";
import {Checkbox} from "primeng/checkbox";
import {CurrencyPipe, DatePipe, NgClass} from "@angular/common";
import {TableModule} from "primeng/table";
import {TravellerDetails, UserTravelItinerary} from '../../../../api';
import {Tab, TabList, Tabs} from 'primeng/tabs';
import {Tooltip} from "primeng/tooltip";
import {itineraryDayRowClass, resolveItineraryTravellerId} from '../../../../util/general.utils';
import {hasOverseasValue} from '../../../../util/itinerary.utils';
import {TaApprovalsTableComponent} from '../ta-approvals-table/ta-approvals-table.component';
import {TRAVEL_APPROVAL_CONTEXTS} from '../../../../util/approval-workflow.utils';

@Component({
  selector: 'manage-ta-acquittal',
    imports: [
        Button,
        ReactiveFormsModule,
        TaCostBubbleComponent,
        Textarea,
        Checkbox,
        FormsModule,
        DatePipe,
        TableModule,
        NgClass,
        CurrencyPipe,
        Tabs,
        Tab,
        TabList,
        Tooltip,
        TaApprovalsTableComponent,
    ],
  templateUrl: './manage-ta-acquittal.component.html',
  styleUrl: './manage-ta-acquittal.component.scss'
})
export class ManageTaAcquittalComponent extends ManageTaBaseComponent implements OnInit {
  readonly travelContexts = TRAVEL_APPROVAL_CONTEXTS;
  travelCertified: boolean = false;
  comments = "";
  partialTaValue = computed<number>(() =>
    (this.store.taValue()?.travellerAmounts ?? {})[this.selectedTravellerId()] ?? 0
  );
  sortedTravellers = computed(() => {
    const primaryId = this.store.taSubmission()?.primaryTraveller?.travellerId;
    return [...(this.taSubmission()?.travellers ?? [])].sort((t1, t2) => {
      if (t1.travellerId === primaryId) return -1;
      if (t2.travellerId === primaryId) return 1;
      if (!t1.details?.surname && !t2.details?.surname) return 0;
      if (!t1.details?.surname) return 1;
      if (!t2.details?.surname) return -1;
      return t1.details.surname.localeCompare(t2.details.surname);
    });
  });
  acquitByPrimary = computed(() => {
    // The primary's own portion is described separately as "your travel portion", so it must not
    // also appear in the "plus the travel portions for:" list. On a delegate TA the primary's
    // isSelfAcquit is false by default, which is what makes this exclusion necessary.
    const primaryId = this.store.taSubmission()?.primaryTraveller?.travellerId;
    return (this.store.taSubmission()?.travellers ?? [])
      .filter(traveller => !traveller.isSelfAcquit && traveller.travellerId !== primaryId);
  })
  // Every portion the booking officer is on the hook for.
  acquitByDelegate = computed(() => {
    return (this.store.taSubmission()?.travellers ?? []).filter(traveller => !traveller.isSelfAcquit);
  })
  // Who acquits the portions the travellers are not acquitting themselves.
  acquitOnBehalfName = computed(() =>
    this.taSubmission()?.delegate?.name
    ?? this.taSubmission()?.primaryTraveller?.details?.name
    ?? 'the primary traveller'
  );

  readonly currentItinerary: Signal<UserTravelItinerary | undefined> = this.store.currentItinerary;
  // Covers the itinerary refetch after a date edit, when the step-container overlay isn't mounted.
  readonly isLoadingData: Signal<boolean> = this.store.isLoadingData;
  protected readonly itineraryDayRowClass = itineraryDayRowClass;
  // Overseas cost column shows when any traveller on this TA has an overseas claim
  showOverseasColumn = computed<boolean>(() =>
    hasOverseasValue(this.currentItinerary()?.items ?? [])
    || this.latestItineraries().some(itinerary => hasOverseasValue(itinerary.items ?? [])));
  selectedTravellerId = signal<string>('');
  selectedTravellerAcquit = computed(() => {
    let traveller = this.taSubmission()?.travellers?.find((traveller) =>
      traveller.travellerId === this.selectedTravellerId()
    );
    return !!(traveller?.acquittalDate);
  });
  selectedTravellerName = computed(() => {
    let traveller = this.taSubmission()?.travellers?.find((traveller) =>
      traveller.travellerId === this.selectedTravellerId()
    );
    if (!traveller) {
      return 'unknown';
    }
    // 'me' only when the session user really is the selected traveller - a booking officer
    // acquitting on someone's behalf must see that person's name.
    if (traveller.details?.id === this.store.user()?.id) {
      return 'me';
    }
    return traveller.details?.name ?? 'unknown';
  })
  singleTravellerValueHeading = computed(() => {
    const name = this.selectedTravellerName();
    return name === 'me'
      ? 'How your TA Acquittal Value was calculated:'
      : `How ${name}'s TA Acquittal Value was calculated:`;
  });
  acquittalRole = computed<'primaryTraveller' | 'secondaryTraveller' | 'viewOnlyTraveller' | 'viewOnly' | 'delegate' | 'adminAcquitter'>(() => {
    let userId = this.store.user()?.id;
    let travellers = this.taSubmission()?.travellers ?? [];
    if (!userId) {
      return 'viewOnly';
    }
    // Strict check: only the booking officer this TA is actually delegated to sees the delegate copy
    if (this.permissionService.isDelegateForTa(this.taSubmission())){
      return 'delegate';
    }
    // System admins, Finance Admins and this TA's booking officer may acquit every portion, but have no delegate-specific copy.
    if (this.permissionService.canBeDelegateForTa(this.taSubmission())) {
      return 'adminAcquitter';
    }
    if (userId === this.taSubmission()?.primaryTraveller?.details?.id) {
      return 'primaryTraveller';
    }
    if (travellers.find((traveller) => traveller.details?.id === userId && traveller.isSelfAcquit)) {
      return 'secondaryTraveller';
    }
    if (travellers.find((traveller) => traveller.details?.id === userId)) {
      return 'viewOnlyTraveller';
    }
    return 'viewOnly';
  });

  canProcessPrepayment = computed(() => this.permissionService.canProcessPrepayment());
  isPrepaymentProcessed = computed(() => {
    return (this.taSubmission()?.approvals?.filter((approval) => (approval.actionContext === "PrePayment" && approval.status === "Actioned" && approval.actionType !== "Cancelled")) ?? []).length > 0
  });

  override ngOnInit() {
    super.ngOnInit();
    // Falls back past the primary traveller so an all-casual delegate TA - which has none - still
    // resolves to a traveller whose itinerary can be fetched.
    const id = resolveItineraryTravellerId(this.taSubmission(), this.store.user()?.id);
    if (id) {
      this.taSubmissionDatasource.loadTravellerItinerary(id);
      this.selectedTravellerId.set(id);
    }
  }

  onTravellerTabChange(event: any) {
    this.selectedTravellerId.set(event);
    this.travelCertified = false;
    this.taSubmissionDatasource.loadTravellerItinerary(event);
  }

  confirmAcquit(): void {
    this.confirmationServiceWrapper.acquitConfirm(this.selectedTravellerId(), this.comments, this.taSubmission());
  }

  allowedTab(traveller: TravellerDetails): boolean {
    // Both roles may acquit every portion and are usually not travellers at all, so the
    // traveller lookup below would lock them out of every tab.
    const role = this.acquittalRole();
    if (role === 'delegate' || role === 'adminAcquitter') {
      return true;
    }
    let currentTraveller = (this.taSubmission()?.travellers ?? []).find((traveller) => (
      this.store.user()!.id === traveller.details?.id
    ));
    if (!currentTraveller) {return false;} //null check

    if (currentTraveller.travellerId === traveller.travellerId) { //Looking at own acquittal
      return true;
    }
    if (!traveller.isSelfAcquit && currentTraveller.travellerId === this.taSubmission()?.primaryTraveller?.travellerId) {
      return true;
    }
    return false;
  }

  confirmProcessPrepayment(): void {
    this.confirmationServiceWrapper.processPrepaymentConfirm().subscribe({
      next: () => {},
      error: err => {
        console.error(err);
      },
    });
  }
}
